import React, { useCallback, useEffect } from 'react';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import Footer from 'components/general/Footer';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { FETCH_DEALERS } from 'actions/dealerEntitlement';

const money = (v) => (_.isNil(v) ? '—' : `₹${Number(v).toFixed(2)}`);

/**
 * Landing page for the programme: one row per dealer with a live summary.
 * Everything else hangs off a dealer, so this is the only sidebar entry.
 */
const DealerList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { navigateTo } = React.useContext(NavContext);

  const dealers = useSelector((state) => state.dealerEntitlement.dealers);
  const isLoader = useSelector((state) => state.dealerEntitlement.isLoading);

  const getAll = useCallback(() => {
    dispatch({ type: FETCH_DEALERS.REQUEST, payload: {} });
  }, [dispatch]);

  useEffect(() => {
    getAll();
  }, [getAll]);

  const openDealer = (dealer) => navigateTo(`/energyCredits/${_.get(dealer, 'id') || _.get(dealer, '_id')}`);

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader title={t('header.energyCredits', 'Energy Credits')} />
        <SkeletonTheme baseColor="#1b2a38" highlightColor="#092235">
          <div className="page-content-wrapper scrollable">
            <div className="user-group-main-block">
              <Card>
                <div className="user-group-block">
                  <div className="user-group-table">
                    <div className="table-responsive">
                      <table className="record-list-table">
                        <thead>
                          <tr>
                            <th>{t('energyCreditDealer.name', 'Dealer')}</th>
                            <th>{t('energyCreditDealer.code', 'Code')}</th>
                            <th>{t('energyCreditDealer.status', 'Status')}</th>
                            <th>{t('energyCreditDealer.enrolments', 'Enrolments')}</th>
                            <th>{t('energyCreditDealer.activePending', 'Active / Pending')}</th>
                            <th>{t('energyCreditDealer.released', 'Released')}</th>
                            <th>{t('energyCreditDealer.used', 'Used')}</th>
                            <th>{t('energyCreditDealer.outstanding', 'Outstanding')}</th>
                            <th className="action-col">{t('energyCreditDealer.action', 'Action')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoader ? (
                            <tr>
                              <td colSpan={9} className="border-0">
                                <Skeleton height={15} />
                              </td>
                            </tr>
                          ) : _.isEmpty(dealers) ? (
                            <tr>
                              <td colSpan={9} className="border-0">
                                <div className="empty-data-block">{t('energyCreditDealer.noDealers', 'No dealers configured')}</div>
                              </td>
                            </tr>
                          ) : (
                            _.map(dealers, (item, index) => (
                              <tr key={`dealer-${index}`} style={{ cursor: 'pointer' }} onClick={() => openDealer(item)}>
                                <td>{_.get(item, 'name', '—')}</td>
                                <td>{_.get(item, 'code', '—')}</td>
                                <td>
                                  {_.get(item, 'is_active') ? t('energyCreditDealer.active', 'Active') : t('energyCreditDealer.paused', 'Paused')}
                                </td>
                                <td>{_.get(item, 'summary.enrolments', 0)}</td>
                                <td>
                                  {_.get(item, 'summary.active', 0)} / {_.get(item, 'summary.pending', 0)}
                                </td>
                                <td>{money(_.get(item, 'summary.released'))}</td>
                                <td>{money(_.get(item, 'summary.used'))}</td>
                                <td>{money(_.get(item, 'summary.outstanding'))}</td>
                                <td>
                                  <Button
                                    className="user-group-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openDealer(item);
                                    }}
                                  >
                                    {t('button.open', 'Open')}
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </SkeletonTheme>
        <Footer />
      </LayoutContainer>
    </React.Fragment>
  );
};

export default DealerList;
